import {
  faArrowDownToBracket,
  faEllipsisVertical,
  faFilter,
  faFolderBookmark,
  faTableLayout,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropdownMenu, IconButton } from '@nexelem/components';
import { ChildrenDrawerPortals } from '@nexelem/drawer';
import { AdvanceFilter, useAdvanceFilters } from '@nexelem/filters';
import { useCustomToast } from '@nexelem/snackbar';
import { useState } from 'react';
import { useCustomTranslation } from '../../../hooks';

type AdvanceFiltersConfig = {
  list: AdvanceFilter<string[]>[];
  openDrawer: () => void;
};

type FilterActionsDropdownProps = {
  onLayoutClick?: () => void;
  onBookmarksClick?: () => void;
  onExport?: () => Promise<void>;
  bookmarksCount?: number;
  advanceFilters?: AdvanceFiltersConfig;
};

export const FilterActionsDropdown = ({
  onLayoutClick,
  onBookmarksClick,
  onExport,
  bookmarksCount = 0,
  advanceFilters,
}: FilterActionsDropdownProps) => {
  const { tWrapper } = useCustomTranslation('lists.filterActions');
  const displayToast = useCustomToast();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const { isApplied } = useAdvanceFilters(advanceFilters?.list ?? []);

  const handleExport = async () => {
    if (!onExport) {
      return;
    }

    setIsLoadingExport(true);
    displayToast({
      message: tWrapper('csvExportStarted'),
      toastType: 'info',
    });

    try {
      await onExport();
      displayToast({
        message: tWrapper('csvExportSuccess'),
        toastType: 'success',
      });
    } catch {
      displayToast({
        message: tWrapper('csvExportError'),
        toastType: 'error',
      });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const savedViewsLabel = `${tWrapper('savedViews')}${bookmarksCount > 0 ? ` (${bookmarksCount})` : ''}`;

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton
            Icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
            withIndicator={advanceFilters && isApplied}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          {advanceFilters && (
            <DropdownMenu.Button StartIcon={<FontAwesomeIcon icon={faFilter} />} onClick={advanceFilters.openDrawer}>
              {tWrapper('advanceFilters')}
            </DropdownMenu.Button>
          )}
          {onLayoutClick && (
            <DropdownMenu.Button StartIcon={<FontAwesomeIcon icon={faTableLayout} />} onClick={onLayoutClick}>
              {tWrapper('columns')}
            </DropdownMenu.Button>
          )}
          {onBookmarksClick && (
            <DropdownMenu.Button StartIcon={<FontAwesomeIcon icon={faFolderBookmark} />} onClick={onBookmarksClick}>
              {savedViewsLabel}
            </DropdownMenu.Button>
          )}
          {onExport && (
            <DropdownMenu.Button
              className="border-t border-border-secondary"
              isLoading={isLoadingExport}
              StartIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
              onClick={handleExport}
            >
              {tWrapper('export')}
            </DropdownMenu.Button>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {advanceFilters && <ChildrenDrawerPortals />}
    </>
  );
};
